import React, { ReactNode } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'

import { Modal, ModalBody, ModalFooter } from '@/components/atoms/modal'
import { StateToggleTab } from '@/components/molecules/settings/state-toggle-tab'
import { createSelfCheckinResponsiveStyle } from '@/utils/front-tablet-responsive-style'
import { DeviceSettingsConnectionTest, DeviceSettingsModalFooter } from './modal-footer'

export type DeviceSettingsTabName = 'Printer' | 'Terminal' | 'CashChanger' | 'Card reader'

export type DeviceSettingsTab = {
  name: DeviceSettingsTabName
  label: string
}

export type DeviceSettingsModalNavigationProps = {
  tabs: DeviceSettingsTab[]
  activeTab: DeviceSettingsTabName
  onTabChange: (tab: DeviceSettingsTabName) => void
  onClose: () => void
}

type Props = DeviceSettingsModalNavigationProps & {
  children: ReactNode
  isDirty: boolean
  onCancel: () => void
  onSave: () => void
  isLoading: boolean
  connectionTest?: DeviceSettingsConnectionTest
}

export const DeviceSettingsModalFrame: React.FC<Props> = ({
  tabs,
  activeTab,
  onTabChange,
  onClose,
  children,
  isDirty,
  onCancel,
  onSave,
  isLoading,
  connectionTest,
}) => {
  const { t } = useTranslation()

  return (
    <Modal customCss={modalStyle} containerCss={modalContainerStyle} isLoading={isLoading}>
      <button type="button" aria-label={t('Close')} css={closeButtonStyle} onClick={onClose}>
        <img src={require('@/static/images/close-camera.svg')} alt="" css={closeIconStyle} />
      </button>
      <ModalBody customCss={modalBodyStyle}>
        <StateToggleTab
          tabs={tabs.map(tab => ({ name: tab.label, value: tab.name }))}
          currentTab={activeTab}
          onChange={onTabChange}
          customTabStyle={tabWidthStyle}
          customActiveTabStyle={activeTabStyle}
          customInactiveTabStyle={inactiveTabStyle}
          customContainerStyle={tabContainerStyle}
        />
        <div css={tabContentStyle}>{children}</div>
      </ModalBody>
      <ModalFooter customCss={modalFooterStyle}>
        <DeviceSettingsModalFooter isDirty={isDirty} onCancel={onCancel} onSave={onSave} connectionTest={connectionTest} />
      </ModalFooter>
    </Modal>
  )
}

const modalStyle = css(
  {
    width: 'min(860px, calc(100vw - 96px))',
    margin: 0,
    maxWidth: 'none',
    borderRadius: 10,
    height: 'calc(100vh - 200px)',
  },
  createSelfCheckinResponsiveStyle({
    portrait: {
      height: '50vh',
    },
  }),
)

const modalContainerStyle = css(
  {
    overflow: 'hidden',
    backgroundColor: 'rgba(39,39,39,0.5)',
    boxSizing: 'border-box',
    padding: '32px 48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  createSelfCheckinResponsiveStyle({
    portrait: {
      paddingTop: 48,
      paddingBottom: 48,
    },
  }),
)

const modalBodyStyle = {
  flex: '1 1 auto',
  minHeight: 0,
  height: 'auto',
  padding: 0,
  display: 'flex',
  flexDirection: 'column' as const,
  overflow: 'hidden',
  borderRadius: '10px 10px 0 0',
}

const modalFooterStyle = {
  boxSizing: 'border-box' as const,
  height: 82,
  padding: '11px 32px',
  flexShrink: 0,
  background: '#FFF',
  border: '1px solid #CCC',
  boxShadow: 'none',
  borderRadius: '0 0 10px 10px',
  ...createSelfCheckinResponsiveStyle({
    landscape13: {
      height: 98,
      padding: '16px 32px',
    },
  }),
}

const tabWidthStyle = css(
  {
    width: '25%',
    height: 65,
    flex: 1,
    flexShrink: 0,
    minWidth: 0,
    borderRadius: '10px 10px 0 0',
    fontSize: 18,
    letterSpacing: '0.9px',
  },
  createSelfCheckinResponsiveStyle({
    landscape13: {
      height: 70,
      fontSize: 22,
    },
  }),
)

const tabContainerStyle = css({ height: 65, flexShrink: 0 }, createSelfCheckinResponsiveStyle({ landscape13: { height: 70 } }))

const activeTabStyle = css({ background: '#FFF', color: '#F2A40B' })
const inactiveTabStyle = css({ background: '#EDEDED', color: '#676767' })

const tabContentStyle = css({
  flex: '0 1 auto',
  height: '100%',
  background: 'white',
  boxShadow: '0px 0px 6px #0000001A',
  borderRadius: 0,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
})

const closeButtonStyle = css(
  {
    position: 'absolute',
    top: -55,
    right: -55,
    zIndex: 104,
    width: 60,
    height: 60,
    padding: 0,
    border: 0,
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  createSelfCheckinResponsiveStyle({ portrait: { right: -10 } }),
)

const closeIconStyle = css({ width: 40, height: 40 })
